import type { JobSummary } from "@contract";
import { useTranslation } from "react-i18next";
import { useJobRetry } from "../../../api/hooks";
import { Button } from "../../../components/control/Button";
import { Card } from "../../../components/data/Card";
import { ErrorBanner } from "../../../components/feedback/ErrorBanner";
import { DateTime } from "../../../components/format/DateTime";
import { RespendConfirmDialog } from "../../../components/overlay/RespendConfirmDialog";
import { useBookContext } from "../bookContext";
import { FAILURES_ANCHOR } from "../chapterAction";

/** The element id the Chapter rows' "see the error" links point at. */
const ANCHOR_ID = FAILURES_ANCHOR.slice(1);

/**
 * One parked job: what stopped, how many attempts it had, and the one retry
 * button for it. The mutation lives here rather than in the card, so a second
 * parked Chapter is not told it is restarting while the first one's POST is in
 * flight, and a retry that comes back 409 or 500 says so instead of leaving a
 * button that quietly re-enables itself.
 */
function ParkedJobRow({ job, title }: { job: JobSummary; title: string }) {
  const { t } = useTranslation(["overview", "errors"]);
  const retry = useJobRetry();
  return (
    <li className="flex flex-col gap-1.5">
      <span className="truncate font-medium text-ink">{title}</span>
      {job.failure ? <span className="text-ink-2">{t(`errors:${job.failure.key}`)}</span> : null}
      <span className="font-mono text-label text-ink-3">
        {t("overview:failures.attempts", {
          attempts: job.attempts,
          maxAttempts: job.maxAttempts,
        })}
        {job.failure ? (
          <>
            {" · "}
            <DateTime value={job.failure.at} style="dateTime" />
          </>
        ) : null}
      </span>
      <Button
        variant="secondary"
        size="sm"
        className="self-start"
        busy={retry.isPending}
        onClick={() => retry.mutate(job.id)}
      >
        {retry.isPending ? t("overview:failures.retrying") : t("overview:failures.retry")}
      </Button>
      {retry.isError ? <ErrorBanner error={retry.error} /> : null}
      {/* A parked render job is the one row here whose retry can cost money twice. */}
      <RespendConfirmDialog state={retry.respend} />
    </li>
  );
}

/**
 * Every job parked for a manual retry, Chapter by Chapter, with the retry
 * that starts it again. The worker gives up after three attempts (DESIGN
 * "States"), and this is where the Author picks the work back up. A Failed
 * Book itself is handled by the banner under the nav.
 */
export function FailuresCard() {
  const { t } = useTranslation("overview");
  const { book } = useBookContext();
  // The job that stopped the whole Book has its retry in the banner under the
  // nav; listing it here too would put two buttons for one job on one screen.
  // Every other parked job is this card's alone: the Chapter row links here
  // rather than carrying a second button of its own.
  const parked = book.activeJobs.filter(
    (job) => job.manualRetryRequired && job.id !== book.retryJobId,
  );
  if (parked.length === 0) return null;
  // One pass over the Chapters for the whole card, rather than a find() per row.
  const titles = new Map<string, string>(
    book.chapters.map((chapter) => [chapter.id, chapter.title]),
  );
  const titleOf = (chapterId: string | null) =>
    (chapterId === null ? undefined : titles.get(chapterId)) ?? book.source.filename;

  return (
    <div id={ANCHOR_ID} className="scroll-mt-24">
      <Card
        label={t("failures.label")}
        tone="warn"
        title={t("failures.chapterTitle", { count: parked.length })}
        titleSize="sm"
      >
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {parked.map((job) => (
            <ParkedJobRow key={job.id} job={job} title={titleOf(job.chapterId)} />
          ))}
        </ul>
      </Card>
    </div>
  );
}
